import {
  DEFAULT_COMPONENT_LABEL_FORMATTING,
  DEFAULT_REFERENCE_LABEL_FORMATTING,
} from 'perspective/perspectiveEditor/labelFormattingUtils';
import {
  getDefaultCompLabelFormattingOpts,
  getDefaultRefLabelFormattingOpts,
  PerspectiveGroupingRule,
  PerspectivesGroupsOptions,
} from '@ardoq/perspectives';
import { CustomLabelFormatting } from '@ardoq/perspectives-sidebar';
import {
  GroupType,
  hasStartQuery,
  hasStartSet,
  isComponent,
  LoadedState,
  TraversalLoadedState,
  MetaModelComponentType,
  ReferenceDirection,
  isTraversalLoadedState,
  isSystemFilter,
  toRequestedPaths,
} from '@ardoq/api-types';
import {
  buildMinimalScopeDataForFilters,
  ConditionalFormattingInfo,
  parseConditionalFormatting,
} from '@ardoq/view-legend';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import {
  ComponentRepresentationData,
  LabelSettingInfo,
  LegacyLabelSettingInfo,
  RuleDescriptionsByEntity,
  ViewpointData,
} from '../appliedSettings/types';
import { filterInterface } from '@ardoq/filter-interface';
import { isNil, noop, omit, sum } from 'lodash';
import { countAppliedFilterRules } from 'loadedState/excludeComponent/excludeComponentOperations';
import { loadedStateOperations } from 'loadedState/loadedStateOperations';
import { hasNamedExpansion } from 'loadedState/isTraversalWithPaths';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { openViewpointBuilder } from 'viewpointBuilder/openViewpointBuilder/openViewpointBuilder';
import { ViewpointBuilderTab } from 'viewpointBuilder/viewpointBuilderNavigation/types';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import { isSingleTraversalState } from './isSingleTraversalState';
import { getIcon } from '@ardoq/icons';
import type { LabelFormattingInfo } from '@ardoq/data-model';
import { exhaustiveCheck, isTruthy } from '@ardoq/common-helpers';
import { trackEventV2 } from 'tracking/tracking';
import { getGraphInterfaceWithModelInterfaces } from '@ardoq/graph';
import { buildSearchData } from 'viewpointBuilder/selectContextTab/buildSearchData';
import { getRepresentationDataWithColorVariantsForComponent } from 'viewpointBuilder/components/getRepresentationDataWithColorVariants';
import { workspaceInterface } from '@ardoq/workspace-interface';

type GetFormattingLabelArgs = {
  appliedFormattingLabel: string;
  customLabels: CustomLabelFormatting[];
  defaultFormattingOpts: CustomLabelFormatting[];
};

const getFormattingLabel = ({
  appliedFormattingLabel,
  customLabels,
  defaultFormattingOpts,
}: GetFormattingLabelArgs) =>
  customLabels.find(({ value }) => value === appliedFormattingLabel) ||
  defaultFormattingOpts.find(({ value }) => value === appliedFormattingLabel);

type LegacyGetAppliedLabelFormattingArgs = {
  appliedComponentFormattingLabel: string;
  appliedReferenceFormattingLabel: string;
  componentFormattingCustomLabels: CustomLabelFormatting[];
  referenceFormattingCustomLabels: CustomLabelFormatting[];
};

type GetAppliedLabelFormattingArgs = {
  appliedLabelFormatting: LabelFormattingInfo[];
  appliedShowReferenceType: boolean;
};

export const getAppliedLabelFormatting = (
  args: (LegacyGetAppliedLabelFormattingArgs | GetAppliedLabelFormattingArgs) &
    WithI18nLocale
): LegacyLabelSettingInfo | LabelSettingInfo => {
  if ('appliedLabelFormatting' in args) {
    const { appliedLabelFormatting, appliedShowReferenceType } = args;
    return {
      labelFormatting: appliedLabelFormatting,
      showReferenceType: appliedShowReferenceType,
    } satisfies LabelSettingInfo;
  }
  const {
    appliedComponentFormattingLabel,
    appliedReferenceFormattingLabel,
    componentFormattingCustomLabels,
    referenceFormattingCustomLabels,
    i18nLocale,
  } = args;

  const referenceFormattingLabel = getFormattingLabel({
    customLabels: referenceFormattingCustomLabels,
    appliedFormattingLabel: appliedReferenceFormattingLabel,
    defaultFormattingOpts: getDefaultRefLabelFormattingOpts(i18nLocale),
  });

  const componentFormattingLabel = getFormattingLabel({
    customLabels: componentFormattingCustomLabels,
    appliedFormattingLabel: appliedComponentFormattingLabel,
    defaultFormattingOpts: getDefaultCompLabelFormattingOpts(i18nLocale),
  });

  const customLabelsCount = [
    appliedComponentFormattingLabel !== DEFAULT_COMPONENT_LABEL_FORMATTING,
    appliedReferenceFormattingLabel !== DEFAULT_REFERENCE_LABEL_FORMATTING,
  ].filter(Boolean).length;

  return {
    referenceFormattingLabel,
    componentFormattingLabel,
    customLabelsCount,
  } satisfies LegacyLabelSettingInfo;
};

type GetEntityName = (
  entityId: string,
  workspaceId: string,
  entityType: 'componentType' | 'referenceType' | 'fieldName' | 'tagName'
) => string | undefined;

const getEntityGetter =
  (groupOptions: PerspectivesGroupsOptions): GetEntityName =>
  (
    entityId: string,
    workspaceId: string,
    entityType: 'componentType' | 'referenceType' | 'fieldName' | 'tagName'
  ) => {
    switch (entityType) {
      case 'componentType':
        return groupOptions.componentTypesPerWorkspace[workspaceId]?.find(
          ({ id }) => id === entityId
        )?.name;
      case 'referenceType':
        return groupOptions.referenceTypesPerWorkspace[workspaceId]?.find(
          ({ id }) => id.toString() === entityId
        )?.name;
      case 'fieldName':
        return groupOptions.fieldsPerWorkspace[workspaceId]?.find(
          ({ id }) => id === entityId
        )?.name;
      case 'tagName':
        return groupOptions.tagsPerWorkspace[workspaceId]?.find(
          ({ id }) => id === entityId
        )?.name;
    }
  };

const getGroupNameString = (i18nLocale: I18nLocale, sublabel?: string) =>
  sublabel
    ? ` ${t(
        i18nLocale,
        'appLayout:groupingRule.withGroupName',
        { sublabel },
        'with group name "{{sublabel}}"'
      )}`
    : '';

const entityAndWorkspaceString = (entity?: string, workspaceName?: string) => {
  if (!entity) {
    return;
  }
  return `"${entity}${workspaceName ? ` (${workspaceName})` : ''}"`;
};

type GetGroupingRuleDescriptionArgs = {
  groupingRule: PerspectiveGroupingRule;
  workspaceNameById: Record<string, string | undefined>;
  getEntityName: GetEntityName;
  i18nLocale: I18nLocale;
};

const getReferenceDirectionLabel = (
  direction: ReferenceDirection,
  i18nLocale: I18nLocale
): string =>
  direction === ReferenceDirection.INCOMING
    ? t(i18nLocale, 'appLayout:groupingRule.incoming', 'Incoming')
    : t(i18nLocale, 'appLayout:groupingRule.outgoing', 'Outgoing');

const getGroupingRuleDescription = ({
  groupingRule,
  workspaceNameById,
  getEntityName,
  i18nLocale,
}: GetGroupingRuleDescriptionArgs): string => {
  if (groupingRule.type === GroupType.WORKSPACE) {
    return t(
      i18nLocale,
      'appLayout:groupingRule.groupByWorkspace',
      'Group by Workspace'
    );
  }
  if (groupingRule.type === GroupType.COMPONENT) {
    return t(
      i18nLocale,
      'appLayout:groupingRule.groupByComponentTypes',
      'Group by Component types'
    );
  }
  if (groupingRule.type === GroupType.REFERENCE) {
    const { sublabel, direction, workspaceId, targetId } = groupingRule;
    const workspaceName = workspaceNameById[workspaceId];

    const referenceType = getEntityName(targetId, workspaceId, 'referenceType');
    const target =
      entityAndWorkspaceString(referenceType, workspaceName) ||
      t(
        i18nLocale,
        'appLayout:groupingRule.noReferenceTypeSelected',
        'No Reference type selected'
      );

    return `${t(
      i18nLocale,
      'appLayout:groupingRule.groupByReference',
      { direction: getReferenceDirectionLabel(direction, i18nLocale), target },
      'Group by {{direction}} reference {{target}}'
    )}${getGroupNameString(i18nLocale, sublabel)}`;
  }

  if (groupingRule.type === GroupType.REFERENCE_TYPE_NAME) {
    const { sublabel, direction, targetId: referenceTypeName } = groupingRule;
    const target =
      referenceTypeName ||
      t(
        i18nLocale,
        'appLayout:groupingRule.noReferenceTypeNameSelected',
        'No Reference type name selected'
      );

    return `${t(
      i18nLocale,
      'appLayout:groupingRule.groupByReferenceTypeName',
      { direction: getReferenceDirectionLabel(direction, i18nLocale), target },
      'Group by {{direction}} reference type name {{target}}'
    )}${getGroupNameString(i18nLocale, sublabel)}`;
  }

  if (groupingRule.type === GroupType.PARENT) {
    const { targetId, workspaceId, sublabel } = groupingRule;
    const workspaceName = workspaceNameById[workspaceId];

    const componentType = getEntityName(targetId, workspaceId, 'componentType');
    const target =
      entityAndWorkspaceString(componentType, workspaceName) ||
      t(
        i18nLocale,
        'appLayout:groupingRule.noComponentTypeSelected',
        'No Component type selected'
      );

    return `${t(
      i18nLocale,
      'appLayout:groupingRule.groupByParent',
      { target },
      'Group by Parent {{target}}'
    )}${getGroupNameString(i18nLocale, sublabel)}`;
  }

  if (groupingRule.type === GroupType.PARENT_ALL) {
    const { sublabel } = groupingRule;

    return `${t(
      i18nLocale,
      'appLayout:groupingRule.groupByParentAll',
      'Group by Parent all'
    )}${getGroupNameString(i18nLocale, sublabel)}`;
  }

  if (groupingRule.type === GroupType.FIELD) {
    const { targetId, workspaceId } = groupingRule;
    const workspaceName = workspaceNameById[workspaceId];
    const fieldName = getEntityName(targetId, workspaceId, 'fieldName');
    const target =
      entityAndWorkspaceString(fieldName, workspaceName) ||
      t(
        i18nLocale,
        'appLayout:groupingRule.noFieldSelected',
        '(no Field selected)'
      );

    return t(
      i18nLocale,
      'appLayout:groupingRule.groupByField',
      { target },
      'Group by Field {{target}}'
    );
  }

  if (groupingRule.type === GroupType.TAG) {
    const { workspaceId, targetId } = groupingRule;
    const workspaceName = workspaceNameById[workspaceId];
    const tagName = targetId && getEntityName(targetId, workspaceId, 'tagName');
    const target =
      entityAndWorkspaceString(tagName, workspaceName) ||
      t(
        i18nLocale,
        'appLayout:groupingRule.noTagSelected',
        '(no Tag selected)'
      );

    return t(
      i18nLocale,
      'appLayout:groupingRule.groupByTag',
      { target },
      'Group by Tag {{target}}'
    );
  }

  if (groupingRule.type === GroupType.CHILD) {
    // ChildGroupingRule has been deprecated. User cannot create a new grouping rule of this type.
    // Added for TypeScript exhaustiveness check
    return t(
      i18nLocale,
      'appLayout:groupingRule.groupByChild',
      'Group by Child'
    );
  }

  exhaustiveCheck(groupingRule);
  return '';
};

const isRulesFromMultipleWorkspaces = (
  groupOptions: PerspectivesGroupsOptions
) => {
  const workspaceIds = new Set<string>();

  for (const groupingRule of groupOptions.appliedGroupingRules) {
    if ('workspaceId' in groupingRule) {
      workspaceIds.add(groupingRule.workspaceId);
    }
    if (workspaceIds.size > 1) {
      return true;
    }
  }

  return false;
};

export const getAppliedGroupingRuleDescriptions = (
  groupOptions: PerspectivesGroupsOptions,
  i18nLocale: I18nLocale
): string[] => {
  const shouldDisplayRuleWorkspace =
    isRulesFromMultipleWorkspaces(groupOptions);

  const workspaceNamesByWorkspaceId = shouldDisplayRuleWorkspace
    ? Object.fromEntries(
        groupOptions.workspaces.map(({ id, name }) => [id, name])
      )
    : {};

  const getEntityName = getEntityGetter(groupOptions);

  return groupOptions.appliedGroupingRules.map(groupingRule =>
    getGroupingRuleDescription({
      groupingRule,
      workspaceNameById: workspaceNamesByWorkspaceId,
      getEntityName,
      i18nLocale,
    })
  );
};

const ruleDescriptionsByEntityReducer = (
  acc: RuleDescriptionsByEntity,
  rules: ConditionalFormattingInfo[]
) => {
  for (const rule of rules) {
    const { filterType, color, filterDescription } = rule;

    if (
      filterType === 'components' ||
      filterType === 'references' ||
      filterType === 'tags'
    ) {
      acc[filterType] = [
        ...(acc[filterType] || []),
        {
          color,
          filterDescription,
        },
      ];
    }
  }

  return acc;
};

export const getAppliedConditionalFormatting = (i18nLocale: I18nLocale) => {
  // Filter out label formatting
  const formattingFilters = filterInterface
    .getFormattingFilters()
    .filter(filter => !isSystemFilter(filter))
    .filter(({ color }) => color);

  if (!formattingFilters.length) {
    return {
      appliedRulesCount: 0,
      ruleDescriptionsByEntity: {},
    };
  }

  const graphInterface = getGraphInterfaceWithModelInterfaces();

  const scopeData = buildMinimalScopeDataForFilters(
    graphInterface,
    formattingFilters
  );

  const ruleDescriptionsByEntity = formattingFilters
    .map(filter => parseConditionalFormatting(scopeData, filter, i18nLocale))
    .reduce(ruleDescriptionsByEntityReducer, {});

  return {
    ruleDescriptionsByEntity,
    appliedRulesCount: sum(
      Object.values(ruleDescriptionsByEntity).map(rules => rules.length)
    ),
  };
};

const getComponentIdsFromLadedState = (entry: TraversalLoadedState) => {
  if (hasStartSet(entry.data.componentSelection)) {
    return entry.data.componentSelection.startSet;
  }
  if (hasStartQuery(entry.data.componentSelection)) {
    return entry.componentIdsAndReferences?.componentIds || [];
  }

  return [];
};
const getComponentTypeRepresentation = (
  entry: TraversalLoadedState,
  componentTypes: MetaModelComponentType[]
): ComponentRepresentationData | undefined => {
  if (!isTraversalLoadedState(entry)) {
    return;
  }

  const componentId = getComponentIdsFromLadedState(entry)[0];
  const typeName = componentInterface.getTypeName(componentId);
  const componentType = componentTypes.find(type => type.name === typeName);

  if (!componentType) {
    return;
  }

  return {
    isImage: Boolean(componentType.style.image) && !componentType.style.icon,
    value: componentType.style.image ?? null,
    icon: getIcon(componentType.style.icon),
    style: { color: componentType.style.color ?? undefined },
  };
};

const getOpenViewpointBuilder =
  (activeTab: ViewpointBuilderTab, entry: TraversalLoadedState) => () => {
    const {
      paths,
      filters,
      pathMatching,
      pathCollapsingRules,
      componentSelection,
    } = entry.data;
    const loadedStateHash = loadedStateOperations.stateToHash(entry);

    const loadedComponents = hasStartSet(componentSelection)
      ? componentSelection.startSet
          .map(id => {
            const component = componentInterface.get(id);
            if (!isComponent(component)) return null;
            return buildSearchData(
              component,
              getRepresentationDataWithColorVariantsForComponent(
                component,
                componentInterface.resolveComponentType(component)
              ),
              workspaceInterface.getWorkspaceName(component.rootWorkspace) ?? ''
            );
          })
          .filter(isTruthy)
      : [];

    const initialConfiguration = {
      componentSelection,
      initialPaths: paths,
      loadedStateHash,
      filters,
      pathMatching,
      pathCollapsingRules,
      loadedComponents,
    };

    openViewpointBuilder({
      activeTab,
      initialConfiguration,
    });
  };

export const getContextComponentTypeSettings = (
  loadedStates: LoadedState[],
  componentTypes: MetaModelComponentType[]
) => {
  if (!isSingleTraversalState(loadedStates)) {
    return {};
  }

  const entry = loadedStates[0];

  return {
    contextComponentType: getStartType(entry),
    representationData: getComponentTypeRepresentation(entry, componentTypes),
  };
};

export const getComponentsAndReferencesTypeSettings = (
  loadedStates: LoadedState[]
) => {
  if (!isSingleTraversalState(loadedStates)) {
    return {
      componentTypesCount: 0,
      referenceTypesCount: 0,
      onEditClick: noop,
    };
  }

  const entry = loadedStates[0];

  const componentTypes = new Set<string>();
  const referenceTypes = new Set<number>();

  loadedStates.forEach(loadedState => {
    if (isTraversalLoadedState(loadedState)) {
      loadedState.componentIdsAndReferences?.componentIds.forEach(id => {
        const typeId = componentInterface.get(id)?.typeId;
        if (typeId) {
          componentTypes.add(typeId);
        }
      });
      loadedState.componentIdsAndReferences?.references.forEach(reference => {
        const typeId = referenceInterface.get(reference._id)?.type;
        if (!isNil(typeId)) {
          referenceTypes.add(typeId);
        }
      });
    }
  });

  const handleComponentAndReferenceTypesEditClick = () => {
    getOpenViewpointBuilder('SUBGRAPH_TAB', entry)();
    trackEventV2({
      event: 'Save As Viewpoint0 Edit component and reference types clicked',
    });
  };
  return {
    componentTypesCount: componentTypes.size,
    referenceTypesCount: referenceTypes.size,
    onEditClick: handleComponentAndReferenceTypesEditClick,
  };
};

export const getFilterSettings = (loadedStates: LoadedState[]) => {
  if (!isSingleTraversalState(loadedStates)) {
    return {
      appliedFiltersCount: 0,
      onEditClick: noop,
    };
  }

  const entry = loadedStates[0];
  const handleFiltersEditClick = () => {
    getOpenViewpointBuilder('FILTERS_TAB', entry)();
    trackEventV2({ event: 'Save As Viewpoint0 Edit filters clicked' });
  };

  /* Exclusions live in the same map and mint one rule per graph node, so a plain sum reported them
     as filters - and the Edit button below opens a Filters tab that cannot show them. */
  const appliedFiltersCount = countAppliedFilterRules(entry.data.filters);

  return {
    appliedFiltersCount,
    onEditClick: handleFiltersEditClick,
  };
};

const getStartType = (entry: TraversalLoadedState) => {
  if (!isTraversalLoadedState(entry)) {
    return;
  }

  // An expansion over several component types holds the sentinel as its paths'
  // start endpoint, and one without hops has no start endpoint at all, so the
  // type it started from is carried beside them.
  if (hasNamedExpansion(entry.data)) {
    return entry.data.startSetExpansionFromType;
  }

  const [firstHop] = entry.data.paths[0] ?? [];

  if (!firstHop) {
    return;
  }

  const { sourceType, targetType, direction } = firstHop;

  return direction === 'incoming' ? targetType : sourceType;
};

export const getViewpointData = (
  loadedStates: LoadedState[]
): ViewpointData => {
  if (!isSingleTraversalState(loadedStates)) {
    return null;
  }

  const entry = loadedStates[0];
  const startType = getStartType(entry);

  if (!startType) {
    return null;
  }

  const conditionalFormatting = filterInterface
    .getFormattingFilters()
    .map(filter => omit(filter, 'id'));

  return {
    startType,
    conditionalFormatting,
    paths: toRequestedPaths(entry.data.paths, entry.data.startSetExpansion),
    traversalId: entry.traversalId ? entry.traversalId : undefined,
    filters: entry.data.filters,
    groupBys: groupingRuleInterface.getActiveGroupingAttributes().groupingRules,
    pathMatching: entry.data.pathMatching,
    pathCollapsingRules: entry.data.pathCollapsingRules,
    ...(entry.data.startSetExpansion
      ? { startSetExpansion: entry.data.startSetExpansion }
      : {}),
  };
};
